import {Component, ElementRef, EventEmitter, Input, Output, ViewChild,} from '@angular/core';
import {AbstractControl, FormsModule, ReactiveFormsModule,} from '@angular/forms';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {ErrorResolver} from '../validation/validation-message-resolver';
import {UtInputComponent, type UtInputType, type UtInputWidth} from 'src/app/ui';
import type {UtIconName} from 'src/app/ui';

export enum InputSize {
  xs = 'xs',
  md = 'md',
  lg = 'lg',
}

/**
 * Legacy `app-input` wrapper — now backed by `UtInputComponent`.
 *
 * Kept as a compatibility shim so the dozens of existing `<app-input>` call
 * sites don't need to change. The inputs that map cleanly to `ut-input` are
 * forwarded; a few niche options (text `prefix`, `cols`, `autoFocus`,
 * `tooltip` help icon) are accepted for compile compatibility but are no-ops
 * and should be removed when call sites get swept to `ut-input` directly.
 *
 * Icon inputs (`iconPrefix`, `iconSuffix`) expect a `UtIconName` — the legacy
 * code passed Material font-icon names like `"help"` or `"home"`, which won't
 * render. Call sites must migrate to icons from `icon-registry.ts` during the
 * Phase 3 vertical sweeps.
 */
@Component({
  selector: 'app-input',
  templateUrl: './input.component.html',
  styleUrls: ['./input.component.scss'],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    TranslatePipe,
    UtInputComponent,
  ],
})
export class InputComponent {
  @Input() control!: AbstractControl;
  @Input() name: string = '';
  @Input() label: string = '';
  @Input() placeholder: string = '';
  @Input() type: string = 'text';
  /** @deprecated text prefixes are no-ops under ut-input — migrate to iconLeft or a hint. */
  @Input() prefix: string = '';
  @Input() iconSuffix?: string;
  @Input() iconPrefix?: string;
  @Input() inline = false;
  @Input() size: InputSize = InputSize.lg;
  @Input() rows = 5;
  /** @deprecated textarea cols are no-ops under ut-input. */
  @Input() cols = 80;
  @Input() autocomplete = 'on';
  /** @deprecated tooltip help-icon is a no-op; use a hint instead. */
  @Input() tooltip: string = '';
  /** @deprecated ut-input doesn't autofocus; trigger `.focus()` on the element from the page. */
  @Input() autoFocus = false;
  @Input() readOnly = false;
  @Input() required = false;
  @Output() keyup = new EventEmitter<KeyboardEvent>();

  /**
   * Retained for callers that do `@ViewChild(InputComponent).inputEl.nativeElement.focus()`.
   * Points at the host `<ut-input>` element so callers that only use `.focus()` still work.
   */
  @ViewChild('input') inputEl!: ElementRef<HTMLElement>;

  constructor(
    public validationMessages: ErrorResolver,
    private translate: TranslateService,
  ) {}

  get utType(): UtInputType {
    const t = this.type;
    if (t === 'textarea' || t === 'email' || t === 'password' || t === 'number' || t === 'search' || t === 'time') {
      return t as UtInputType;
    }
    return 'text';
  }

  get utWidth(): UtInputWidth {
    switch (this.size) {
      case InputSize.xs:
        return 'sm';
      case InputSize.md:
        return 'md';
      default:
        return 'full';
    }
  }

  get translatedLabel(): string {
    return this.label ? this.translate.instant(this.label) : '';
  }

  get translatedPlaceholder(): string {
    return this.placeholder ? this.translate.instant(this.placeholder) : '';
  }

  get errorMessage(): string {
    if (!this.control) return '';
    if (!this.control.touched) return '';
    const errors = this.control.errors;
    if (!errors) return '';
    const messages = this.validationMessages.getMessagesFor(errors);
    return messages[0] ?? '';
  }

  get iconLeft(): UtIconName | undefined {
    return this.iconPrefix as UtIconName | undefined;
  }

  get iconRight(): UtIconName | undefined {
    return this.iconSuffix as UtIconName | undefined;
  }

  onKeyUp(ev: KeyboardEvent): void {
    if (this.keyup.observers.length > 0) {
      this.keyup.emit(ev);
      ev.stopPropagation();
    }
  }
}
